export interface SpeakingAgent {
  id: string;
  name: string;
  status: 'speaking' | 'waiting' | 'done';
}

export interface TypingFeedEntry {
  agent_id: string;
  agent_name?: string;
  content: string;
  message_type: string;
  timestamp?: number;
}

interface NodeLike {
  id: string;
  label?: string;
  group?: 'station' | 'device';
  stationId?: string;
  deviceType?: string;
}

function normalizeKey(value?: string): string {
  return (value ?? '').trim().toLowerCase();
}

function compactKey(value?: string): string {
  return normalizeKey(value).replace(/[^\p{L}\p{N}]+/gu, '');
}

function keyVariants(value?: string): string[] {
  const normalized = normalizeKey(value);
  if (!normalized) return [];

  const variants = new Set<string>([normalized, normalized.replace(/_/g, '-')]);
  const withoutPrefix = normalized.replace(/^(device|manager|station|master)[_:-]+/, '');
  variants.add(withoutPrefix);
  variants.add(withoutPrefix.replace(/_/g, '-'));

  return [...variants].filter(Boolean);
}

function delimitedIncludes(haystack: string, needle: string): boolean {
  if (!haystack || !needle) return false;
  if (haystack === needle) return true;
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(needle)}($|[^\\p{L}\\p{N}])`, 'iu').test(haystack);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Strip master-forwarded station prefix, e.g. `lakhok/device_lakhok_p-1` → `device_lakhok_p-1`. */
export function stripStationScopedAgentId(agentId?: string): string {
  if (!agentId) return '';
  const slash = agentId.indexOf('/');
  if (slash >= 0) return agentId.slice(slash + 1);
  return agentId;
}

export function parseDeviceAgentRef(
  agentId: string,
): { stationId: string; deviceId: string } | null {
  const id = normalizeKey(stripStationScopedAgentId(agentId));
  const match = id.match(/^device[_-]([^_-]+)[_-](.+)$/);
  if (!match) return null;
  return { stationId: match[1], deviceId: match[2] };
}

export function parseManagerStationRef(agentId: string): string | null {
  const id = normalizeKey(stripStationScopedAgentId(agentId));
  const match = id.match(/^manager[_-](.+)$/);
  return match ? match[1] : null;
}

function isBubbleWorthy(entry: Pick<TypingFeedEntry, 'message_type' | 'content'>): boolean {
  return entry.content.trim().length > 0;
}

export function isManagerAgent(
  agent?: Pick<SpeakingAgent, 'id' | 'name'> | Pick<TypingFeedEntry, 'agent_id' | 'agent_name' | 'message_type'>,
): boolean {
  if (!agent) return false;
  const id = 'agent_id' in agent ? agent.agent_id : agent.id;
  const name =
    'agent_name' in agent ? agent.agent_name : ('name' in agent ? agent.name : undefined);
  const type = 'message_type' in agent ? agent.message_type : '';
  const keys = [id, name].map(normalizeKey);

  if (type === 'employee_direct' || type === 'chat_response') return false;
  if (keys.some((key) => key.includes('employee'))) return false;

  return (
    type === 'manager_contribution' ||
    type === 'manager_discussion' ||
    keys.some(
      (key) =>
        key === 'manager' ||
        key === 'assistant' ||
        key === 'agent' ||
        key === 'master' ||
        key.includes('manager') ||
        key.includes('master'),
    )
  );
}

export function nodeMatchesAgent(
  node: NodeLike,
  agent: Pick<SpeakingAgent, 'id' | 'name'> | Pick<TypingFeedEntry, 'agent_id' | 'agent_name' | 'message_type' | 'content'>,
): boolean {
  if (node.group === 'station') {
    if (!isManagerAgent(agent)) return false;

    const stationKeys = [node.stationId, node.label].flatMap(keyVariants);
    const agentKeys = getAgentKeys(agent);
    const managerStation = parseManagerStationRef(
      'agent_id' in agent ? agent.agent_id : agent.id,
    );
    if (managerStation && node.stationId && normalizeKey(managerStation) === normalizeKey(node.stationId)) {
      return true;
    }

    const isGenericManager = agentKeys.some(
      (key) => key === 'manager' || key === 'assistant' || key === 'agent' || key === 'master',
    );
    if (isGenericManager) return true;

    return (
      stationKeys.length === 0 ||
      agentKeys.length === 0 ||
      agentKeys.some((key) => key.startsWith('manager') || key.startsWith('master')) ||
      stationKeys.some((key) => agentKeys.some((agentKey) => agentKey.includes(key)))
    );
  }

  if (node.group === 'device' && isManagerAgent(agent)) return false;

  const nodeKeys = [node.id, node.label, node.stationId, node.deviceType].flatMap(keyVariants);
  const nodeCompacts = nodeKeys.map(compactKey).filter(Boolean);
  const agentKeys = getAgentKeys(agent);
  const agentCompacts = agentKeys.map(compactKey).filter(Boolean);

  if (node.group === 'device') {
    const rawAgentId = 'agent_id' in agent ? agent.agent_id : agent.id;
    const deviceRef = parseDeviceAgentRef(rawAgentId || '');
    if (deviceRef && node.stationId && node.label) {
      const stationMatch = normalizeKey(deviceRef.stationId) === normalizeKey(node.stationId);
      const deviceKeys = [node.label, node.id].flatMap(keyVariants);
      const deviceIdMatch = deviceKeys.some(
        (deviceKey) =>
          normalizeKey(deviceRef.deviceId) === normalizeKey(deviceKey) ||
          compactKey(deviceRef.deviceId) === compactKey(deviceKey) ||
          delimitedIncludes(normalizeKey(deviceRef.deviceId), normalizeKey(deviceKey)),
      );
      if (stationMatch && deviceIdMatch) return true;
    }

    const nodeIdCompact = compactKey(node.id);
    const nodeLabelCompact = compactKey(node.label);
    const agentName =
      ('agent_name' in agent ? agent.agent_name : ('name' in agent ? agent.name : '')) || '';
    const agentNameCompact = compactKey(agentName);

    for (const ak of agentKeys) {
      if (ak === nodeIdCompact || ak === nodeLabelCompact) return true;
    }
    if (agentNameCompact === nodeIdCompact || agentNameCompact === nodeLabelCompact) return true;

    const deviceKeys = [node.label, node.id].flatMap(keyVariants);
    const deviceCompacts = deviceKeys.map(compactKey).filter(Boolean);
    const stationKeys = keyVariants(node.stationId);
    const agentNameKeys = keyVariants(agentName);
    const agentNameCompacts = agentNameKeys.map(compactKey).filter(Boolean);

    return (
      agentKeys.some((agentKey) => {
        if (deviceKeys.some((deviceKey) => delimitedIncludes(agentKey, deviceKey))) return true;
        if (stationKeys.some((stationKey) => delimitedIncludes(agentKey, stationKey))) {
          return deviceKeys.some((deviceKey) => agentKey.endsWith(deviceKey));
        }
        const agentCompact = compactKey(agentKey);
        return deviceCompacts.some((deviceCompact) => agentCompact.endsWith(deviceCompact));
      }) ||
      agentNameKeys.some((nameKey) => {
        if (deviceKeys.some((deviceKey) => delimitedIncludes(nameKey, deviceKey))) return true;
        return deviceCompacts.some(
          (deviceCompact) => compactKey(nameKey).endsWith(deviceCompact),
        );
      }) ||
      agentNameCompacts.some((nameCompact) =>
        deviceCompacts.some(
          (deviceCompact) => nameCompact.endsWith(deviceCompact) || nameCompact === deviceCompact,
        ),
      )
    );
  }

  return (
    nodeKeys.some((nodeKey) => agentKeys.some((agentKey) => delimitedIncludes(agentKey, nodeKey))) ||
    nodeCompacts.some((nodeCompact) =>
      agentCompacts.some(
        (agentCompact) => agentCompact === nodeCompact || agentCompact.endsWith(nodeCompact),
      ),
    )
  );
}

export function findLatestNodeDiscussionEntry(
  node: NodeLike,
  feed: TypingFeedEntry[],
): TypingFeedEntry | undefined {
  const TTL_MS = 10 * 60_000;
  const now = Date.now();
  for (let index = feed.length - 1; index >= 0; index -= 1) {
    const entry = feed[index];
    if (!isBubbleWorthy(entry)) continue;
    if (nodeMatchesAgent(node, entry)) {
      const ts = entry.timestamp ?? now;
      if (now - ts <= TTL_MS) return entry;
    }
  }
  return undefined;
}

export function formatNodeBubbleText(content: string, maxChars = 78): string {
  const stripped = content
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(\*|_)(.+?)\1/g, '$2')
    .replace(/\s+/g, ' ')
    .trim();

  if (stripped.length <= maxChars) return stripped;
  return `${stripped.slice(0, maxChars - 1).trim()}...`;
}

export function splitBubbleText(text: string, maxLineChars = 28): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxLineChars) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    current = word.length > maxLineChars ? word.slice(0, maxLineChars - 1) : word;
  });

  if (current) lines.push(current);
  return lines.slice(0, 2);
}

function getAgentKeys(
  agent: Pick<SpeakingAgent, 'id' | 'name'> | Pick<TypingFeedEntry, 'agent_id' | 'agent_name' | 'message_type' | 'content'>,
): string[] {
  const id = 'agent_id' in agent ? agent.agent_id : agent.id;
  const name =
    'agent_name' in agent ? agent.agent_name : ('name' in agent ? agent.name : undefined);
  const strippedId = stripStationScopedAgentId(id);
  return [id, strippedId, name].flatMap(keyVariants);
}
